/**
 * SavoirCareBoardColumn.jsx — a single Kanban column on the SAVOIR CARE board.
 *
 * What: Renders a stage header (label + count) and a droppable list of patient
 *       cards for that stage. Used for both clinical stages and the visually
 *       distinct terminal Patient-Elected Redirect column.
 * Why:  Encapsulates the @dnd-kit droppable zone + SortableContext so the board
 *       page just maps stages → columns.
 * Invariants:
 *   - The redirect column is rendered with a gray accent and is NOT a drop target
 *     (entering redirect requires the documentation modal).
 *
 * Ported from HealthDesk Experimental (Exp-B) — no adaptation.
 * Tightest coupling: SavoirCareBoardPage.jsx, SavoirCarePatientCard.jsx.
 */

import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Box, Paper, Typography, Chip, useTheme } from '@mui/material';
import SavoirCarePatientCard from './SavoirCarePatientCard';
import {
  SAVOIR_CARE_STAGE_ROLE,
  SAVOIR_CARE_STAGE_ROLE_LABELS,
} from '../constants/savoirCareStages';
import { getStageAccentColor } from '../utils/savoirCareTheme';

/** Min/preferred column width so columns scroll horizontally rather than squish. */
const COLUMN_WIDTH = 260;

const SavoirCareBoardColumn = ({
  stage,
  label,
  entries,
  canWrite,
  isRedirectColumn = false,
  isIncomingColumn = false,
  isExpectedColumn = false,
  now,
  onAdvance,
  onDismissExpected,
  onFlag,
  onEscalate,
  onRedirect,
  onFlagEmergencyIntervention,
  onClearEmergencyIntervention,
  onOpenChartMind,
  onSetLocation,
}) => {
  const theme = useTheme();

  // Neither the terminal redirect column nor the pre-arrival Expected/Incoming
  // columns are drop targets: redirect is entered only via the documentation
  // modal, and you cannot drag a patient back into pre-arrival lanes.
  const { setNodeRef, isOver } = useDroppable({
    id: stage,
    disabled: isRedirectColumn || isIncomingColumn || isExpectedColumn,
  });

  // Accent color: teal (expected) / purple (incoming) / gray (redirect) / primary.
  const accentColor = getStageAccentColor(theme, {
    isExpected: isExpectedColumn,
    isIncoming: isIncomingColumn,
    isRedirect: isRedirectColumn,
  });

  // Results-Pending Lounge count for this column: entries whose physical_location
  // contains "lounge" (case-insensitive). Display only.
  const loungeCount = entries.filter(
    (e) => (e.physical_location || '').toLowerCase().includes('lounge')
  ).length;

  // Per-column staffing role badge (physician-bookend model).
  const roleKey = SAVOIR_CARE_STAGE_ROLE[stage];
  const roleLabel = SAVOIR_CARE_STAGE_ROLE_LABELS[roleKey];
  const showRoleBadge = roleKey === 'physician' || roleKey === 'app' || roleKey === 'front_desk';

  return (
    <Paper
      elevation={0}
      data-testid={`savoir-care-column-${stage}`}
      sx={{
        width: COLUMN_WIDTH,
        minWidth: COLUMN_WIDTH,
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid',
        borderColor: 'divider',
        borderTop: '3px solid',
        borderTopColor: accentColor || 'primary.main',
        borderRadius: 2,
        bgcolor: (isRedirectColumn || isIncomingColumn || isExpectedColumn) ? 'action.hover' : 'background.default',
      }}
    >
      <Box sx={{ p: 1.5, pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, color: accentColor || 'text.primary' }}
          >
            {label}
          </Typography>
          <Chip label={entries.length} size="small" />
          {loungeCount > 0 && (
            <Typography
              variant="caption"
              sx={{ color: theme.palette.board?.lounge?.color, fontWeight: 600 }}
            >
              · {loungeCount} in lounge
            </Typography>
          )}
        </Box>
        {showRoleBadge && (
          <Chip
            label={roleLabel}
            size="small"
            variant="outlined"
            sx={{
              mt: 0.5,
              height: 18,
              fontSize: '0.65rem',
              fontWeight: 600,
              letterSpacing: 0.4,
              textTransform: 'uppercase',
              color: roleKey === 'physician' ? 'primary.main' : 'text.secondary',
              borderColor: roleKey === 'physician' ? 'primary.main' : 'divider',
            }}
          />
        )}
      </Box>

      <Box
        ref={setNodeRef}
        sx={{
          flex: 1,
          minHeight: 120,
          p: 1.5,
          pt: 0.5,
          overflowY: 'auto',
          bgcolor: isOver ? 'action.selected' : 'transparent',
          borderRadius: 1,
          transition: 'background-color 120ms ease',
        }}
      >
        <SortableContext
          items={entries.map((e) => e.entryId)}
          strategy={verticalListSortingStrategy}
        >
          {entries.length === 0 ? (
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
              No patients
            </Typography>
          ) : (
            entries.map((entry) => (
              <SavoirCarePatientCard
                key={entry.entryId}
                entry={entry}
                canWrite={canWrite}
                now={now}
                onAdvance={onAdvance}
                onDismissExpected={onDismissExpected}
                onFlag={onFlag}
                onEscalate={onEscalate}
                onRedirect={onRedirect}
                onFlagEmergencyIntervention={onFlagEmergencyIntervention}
                onClearEmergencyIntervention={onClearEmergencyIntervention}
                onOpenChartMind={onOpenChartMind}
                onSetLocation={onSetLocation}
              />
            ))
          )}
        </SortableContext>
      </Box>
    </Paper>
  );
};

export default SavoirCareBoardColumn;
